import { cn } from '@/lib/utils';
import { fmtTime } from '@/lib/format';
import { ErrorState } from '@/components/shared/states';
import type { AvailabilitySlot } from './api';

export interface PickedSlot {
  startsAt: string;
  barberId: string;
}

/** Horários reais vindos de /appointments/availability. */
export function SlotPicker({
  slots,
  loading,
  error,
  onRetry,
  selected,
  onSelect,
  tz,
  barberNames,
  showBarber,
  extra,
}: {
  slots: AvailabilitySlot[] | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  selected: PickedSlot | null;
  onSelect: (s: PickedSlot) => void;
  tz: string;
  barberNames: Map<string, string>;
  showBarber: boolean;
  /** horário pré-selecionado fora da lista (clique na grade) */
  extra?: PickedSlot | null;
}) {
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (loading) return <p className="text-sm text-muted-foreground" role="status">Buscando horários livres…</p>;
  const list = [...(slots ?? [])];
  if (extra && !list.some((s) => s.startsAt === extra.startsAt && s.barberId === extra.barberId)) {
    list.push({ startsAt: extra.startsAt, endsAt: extra.startsAt, barberId: extra.barberId });
    list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }
  if (!list.length) return <p className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">Sem horários livres neste dia. Tente outra data ou outro profissional.</p>;
  return (
    <div role="radiogroup" aria-label="Horários disponíveis" className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
      {list.map((s) => {
        const active = selected?.startsAt === s.startsAt && selected.barberId === s.barberId;
        return (
          <button
            key={`${s.startsAt}-${s.barberId}`}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onSelect({ startsAt: s.startsAt, barberId: s.barberId })}
            className={cn(
              'rounded-md border px-2 py-1.5 text-sm transition focus-visible:outline-2 focus-visible:outline-ring',
              active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-accent',
            )}
          >
            <span className="tabular font-medium">{fmtTime(s.startsAt, tz)}</span>
            {showBarber && <span className={cn('block truncate text-xs', active ? 'text-primary-foreground/80' : 'text-muted-foreground')}>{barberNames.get(s.barberId) ?? ''}</span>}
          </button>
        );
      })}
    </div>
  );
}
